/**
 * FinanceAPIs client.
 * Typed wrapper around SEC EDGAR API endpoints.
 */

import { clientFetch, type Result } from './api-client.js';
import type {
  SubmissionsResponse,
  HoldingsResponse,
  FilersResponse,
} from '../../shared/api-types.js';

const BASE = '/api/v1/edgar';

/**
 * Fetch 13F submissions for a filer.
 * Precondition: cik is a valid 10-digit CIK
 * Postcondition: Returns submissions data or error
 */
export function fetchSubmissions(cik: string): Promise<Result<SubmissionsResponse>> {
  return clientFetch<SubmissionsResponse>(`${BASE}/forms/13f/submissions/${cik}`);
}

/**
 * Fetch 13F holdings comparison between two quarters.
 * Precondition: cik is valid, q1/q2 are quarter strings like "2024-Q1"
 * Postcondition: Returns holdings with deltas or error
 */
export function fetchHoldings(
  cik: string,
  q1: string,
  q2: string
): Promise<Result<HoldingsResponse>> {
  return clientFetch<HoldingsResponse>(`${BASE}/forms/13f/holdings/${cik}/${q1}/${q2}`);
}

/**
 * Search for filers by name or CIK.
 * Precondition: query is non-empty string
 * Postcondition: Returns matching filers or error
 */
export function searchFilers(query: string): Promise<Result<FilersResponse>> {
  return clientFetch<FilersResponse>(`${BASE}/filers/search?q=${encodeURIComponent(query)}`);
}
